import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedLink from './LocalizedLink.js';

const STORAGE_KEY = 'cookie_consent';

// Updates Google Consent Mode. public/index.html defines window.gtag before
// GTM loads (and sets the initial default from localStorage), so this reuses
// the same call shape GTM already understands rather than touching
// dataLayer directly.
function updateConsent(granted) {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
    });
  }
}

// Bottom cookie banner gating GTM/GA4 analytics cookies. Shown once, until
// the visitor makes a choice; the choice is then remembered in localStorage
// and re-applied on future loads via the inline script in public/index.html.
const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let stored = null;
    try {
      stored = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      // localStorage unavailable (private mode, blocked storage, etc.) —
      // treat as no stored choice and keep analytics denied.
    }
    if (stored !== 'granted' && stored !== 'denied') {
      setVisible(true);
    }
  }, []);

  const choose = (granted) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, granted ? 'granted' : 'denied');
    } catch (e) {
      // Choice won't persist across reloads, but still apply it this session.
    }
    updateConsent(granted);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 bg-gray-900 text-gray-100 p-4 shadow-2xl">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center gap-4">
        <p className="text-sm flex-1 text-center sm:text-left">
          {t('cookieConsent.message')}{' '}
          <LocalizedLink to="/privacy-policy" className="underline hover:text-red-400">
            {t('cookieConsent.privacyPolicy')}
          </LocalizedLink>
        </p>
        <div className="flex gap-3 shrink-0">
          <button
            onClick={() => choose(false)}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-700 hover:bg-gray-600 transition"
          >
            {t('cookieConsent.decline')}
          </button>
          <button
            onClick={() => choose(true)}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 hover:bg-green-700 transition"
          >
            {t('cookieConsent.accept')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
